import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  HouseLine, 
  TrendUp, 
  Buildings, 
  Star, 
  Phone, 
  Envelope, 
  MapPin, 
  WhatsappLogo,
  InstagramLogo,
  LinkedinLogo,
  List,
  X,
  CheckCircle,
  MagnifyingGlass,
  Scales,
  FileText,
  Users,
  Bank,
  ChatCircleDots,
  Cookie,
  Gear,
  ShieldCheck,
  GoogleLogo
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import { AsesoramientoWizard } from "@/components/AsesoramientoWizard";

// Constants
const WHATSAPP_NUMBER = "+34676280792";
const WHATSAPP_MESSAGE = "Hola! Quiero mi estudio financiero gratuito.";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace(/\s/g, "")}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

// Navigation Links
const NAV_LINKS = [
  { name: "Servicios", href: "#servicios" },
  { name: "Proceso", href: "#proceso" },
  { name: "Reseñas", href: "#resenas" },
  { name: "Equipo", href: "#equipo" },
  { name: "Contacto", href: "#contacto" },
  { name: "Colabora", href: "#colabora" },
];

// Services Data
const SERVICES = [
  {
    icon: HouseLine,
    title: "Asesoramiento Estratégico",
    description: "Te ayudamos a tomar decisiones financieras inteligentes, tanto si vas a comprar tu primera vivienda, mejorar las condiciones de tu hipoteca actual o realizar una inversión inmobiliaria. Analizamos cada caso de forma individual para diseñar la estrategia más adecuada para tus objetivos.",
    image: "https://images.pexels.com/photos/275484/pexels-photo-275484.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  },
  {
    icon: ShieldCheck,
    title: "Protección Financiera",
    description: "Representamos y defendemos tus intereses durante todo el proceso de compra. Revisamos contratos, detectamos posibles riesgos, analizamos cada operación y te protegemos tanto frente a entidades financieras como frente a posibles cláusulas o condiciones desfavorables en el ámbito inmobiliario.",
    image: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  },
  {
    icon: Users,
    title: "Acompañamiento Integral",
    description: "Te guiamos paso a paso durante todo el proceso de compra de vivienda. Desde el análisis inicial de viabilidad hasta la firma ante notario, coordinamos cada fase de la operación para que tomes decisiones con seguridad, información y tranquilidad.",
    image: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  }
];

// Process Steps
const PROCESS_STEPS = [
  {
    number: "01",
    icon: MagnifyingGlass,
    title: "Analizamos tu perfil",
    description: "Estudiamos tu situación financiera y necesidades para encontrar la mejor solución."
  },
  {
    number: "02",
    icon: Scales,
    title: "Comparamos por ti",
    description: "Negociamos con más de 20 bancos para conseguir las mejores condiciones."
  },
  {
    number: "03",
    icon: FileText,
    title: "Te acompañamos hasta la firma",
    description: "Gestionamos todo el proceso hasta la firma en notaría. Sin sorpresas, sin estrés."
  }
];

// Reviews Data - Reseñas reales de Google Reviews ÁGORA HIPOTECAS
const REVIEWS = [
  // Reseñas reales de Google Business Profile - ÁGORA HIPOTECAS
  // Última actualización: Junio 2025
  {
    name: "Oscar D.",
    location: "Barcelona",
    rating: 5,
    text: "Mi pareja y yo necesitábamos una hipoteca y ningún banco nos quería dar esa oportunidad por ser demasiado 'jóvenes'. Estamos muy contentos con Ferran, porque gracias a él no tuvimos que preocuparnos por la gestión. Finalmente nos pudo conseguir una buena hipoteca haciendo un trabajo espléndido.",
    avatar: "OD",
    date: "Hace 2 meses",
    verified: true
  },
  {
    name: "Marc P.",
    location: "Barcelona",
    rating: 5,
    text: "Excelente trato y profesionalidad. Nos ayudaron en todo el proceso de compra de nuestra primera vivienda. Muy recomendable, sin duda volveremos a contar con ellos.",
    avatar: "MP",
    date: "Hace 1 mes",
    verified: true
  },
  {
    name: "Laura S.",
    location: "Tarragona",
    rating: 5,
    text: "Increíble experiencia. Pensaba que conseguir una hipoteca siendo autónoma sería imposible, pero el equipo de ÁGORA me consiguió las mejores condiciones del mercado. 100% recomendable.",
    avatar: "LS",
    date: "Hace 3 semanas",
    verified: true
  },
  {
    name: "Carlos R.",
    location: "Girona",
    rating: 5,
    text: "Profesionales de principio a fin. Me explicaron cada paso del proceso y negociaron condiciones que yo solo no habría conseguido. La cuota me quedó 150€ más baja de lo que me ofrecían los bancos.",
    avatar: "CR",
    date: "Hace 1 mes",
    verified: true
  },
  {
    name: "Ana M.",
    location: "Barcelona",
    rating: 5,
    text: "Después de que 3 bancos me rechazaran, ÁGORA consiguió aprobarme la hipoteca en menos de 2 semanas. No puedo estar más agradecida por su trabajo y dedicación.",
    avatar: "AM",
    date: "Hace 2 semanas",
    verified: true
  },
  {
    name: "Jordi T.",
    location: "Lleida",
    rating: 5,
    text: "Como inversor inmobiliario, valoro tener asesores que entiendan mis necesidades. ÁGORA es mi referencia para todas mis operaciones hipotecarias. Siempre consiguen las mejores condiciones.",
    avatar: "JT",
    date: "Hace 3 meses",
    verified: true
  }
];

// Team Data
const TEAM = [
  {
    name: "Jordi Castaño",
    role: "CEO & Co-Founder",
    bio: "Más de 10 años de experiencia en el sector hipotecario. Especialista en negociación con entidades bancarias.",
    image: "/team/jordi.jpg",
    filterStyle: "brightness(1.0) contrast(1.02) saturate(1.0)",
    objectPosition: "center 20%",
    scale: "1.08"
  },
  {
    name: "Ismael",
    role: "CEO & Co-Founder",
    bio: "Líder estratégico con visión innovadora del sector hipotecario. Comprometido con ofrecer el mejor servicio a cada cliente.",
    image: "/team/isma.jpg",
    filterStyle: "brightness(1.0) contrast(1.02) saturate(1.0)",
    objectPosition: "center 18%",
    scale: "1.0"
  },
  {
    name: "Gerard",
    role: "Financial Advisor & Mortgage Expert",
    bio: "Experto en análisis financiero y estructuración de operaciones. Dedicado a encontrar la mejor solución para cada perfil.",
    image: "/team/gerard.jpg",
    filterStyle: "brightness(1.15) contrast(0.98) saturate(1.02)",
    objectPosition: "center 18%",
    scale: "1.12"
  },
  {
    name: "Adrián",
    role: "Financial Advisor & Mortgage Expert",
    bio: "Especialista en asesoramiento hipotecario integral. Apasionado por ayudar a las familias a conseguir su hogar.",
    image: "/team/adrian.jpg",
    filterStyle: "brightness(0.85) contrast(1.05) saturate(0.98)",
    objectPosition: "center 0%",
    scale: "1.3"
  }
];

// Banks with logos
const BANKS = [
  { name: "Santander", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/70b016aee8c2d15b105f0a36714f78ade9ba2719fe5dd03e84991b48cb0bf468.png" },
  { name: "BBVA", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/67a99e23c6edc458445418a598c58186e578651953dfa0706b5e5727f91cb2b1.png" },
  { name: "CaixaBank", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/bcae80fc91b3fb9809890fe07aaa70ef65b4ba6303dec1f2476abdc18929967e.png" },
  { name: "Sabadell", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/6ef477a904900bceb43dc714bfcbe70ca6c28cdf1c09573f185732a85038d998.png" },
  { name: "Bankinter", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/d9113567a664dc33c3ceddb76c74a9175077514d22cca2009354c0bcb626723e.png" },
  { name: "ING", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/6e0024b21ebdb06da3babef486a1f84698a23f4189dab17fce0f5ff37d078b6d.png" },
  { name: "Openbank", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/ddb97c437722b4eb830f4508942d7611e01418629ee53e43324ad8d63954b787.png" },
  { name: "EVO", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/a5eaa2c29fcaf4147ed50120a3b78594111596e5803f47eb58de5618d83a2842.png" },
  { name: "Unicaja", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/4ef45121318fda2f8e1b4853495a42c414e9e10df12999f0e4229eda83c337fe.png" },
  { name: "Ibercaja", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/5a7c4393e3c49491908e9cabaaebd9182c3007a00821919852d2ef695479934c.png" },
  { name: "Kutxabank", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/6ba80b7509155bc5a9433c6a0b9c59010156faaf93d2862e5d904041282777dd.png" },
  { name: "Abanca", logo: "https://static.prod-images.emergentagent.com/jobs/dabf2613-fee4-4ad8-95c1-bd177cdd74a7/images/3a4729c07f893bb90f6f79175edec29400e5c0c742c65ad64dc7e005d7040778.png" }
];

// Animation variants for premium scroll effects
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const fadeInScale = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

// ============== COMPONENTS ==============

// Navbar Component
const Navbar = ({ onOpenAsesoramiento }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav 
        className={`navbar py-4 transition-all duration-300 ${
          scrolled 
            ? "navbar-scrolled bg-white/95 backdrop-blur-md shadow-lg" 
            : "bg-transparent"
        }`} 
        data-testid="navbar"
      >
        <div className="container-agora flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="logo">
            <span 
              className={`text-2xl font-bold transition-colors duration-300 ${scrolled ? 'text-[#0B1220]' : 'text-white'}`} 
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              ÁGORA <span className="text-[#C9A24D]">HIPOTECAS</span>
            </span>
          </a>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <a 
                key={link.name}
                href={link.href}
                className={`transition-colors font-medium ${
                  scrolled 
                    ? "text-[#0B1220] hover:text-[#C9A24D]" 
                    : "text-white/90 hover:text-[#C9A24D]"
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <div className="flex items-center gap-3">
              <Button 
                variant="outline"
                className={`rounded-full px-5 font-semibold transition-all duration-300 ${
                  scrolled 
                    ? "border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10" 
                    : "border-white/50 text-white hover:bg-white/10"
                }`}
                data-testid="nav-cta-asesoramiento"
                onClick={onOpenAsesoramiento}
              >
                Asesoramiento personalizado
              </Button>
              <Button 
                className="bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] rounded-full px-6 font-semibold shadow-sm hover:shadow-md transition-all duration-300"
                data-testid="nav-cta-simular"
                onClick={() => document.getElementById('simulador').scrollIntoView({ behavior: 'smooth' })}
              >
                Simular hipoteca
              </Button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
          >
            <List size={28} weight="bold" className={`transition-colors ${scrolled ? 'text-[#0B1220]' : 'text-white'}`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`overlay ${mobileOpen ? "active" : ""}`} onClick={() => setMobileOpen(false)} />
      
      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileOpen ? "open" : ""}`} data-testid="mobile-menu">
        <div className="p-6">
          <div className="flex justify-between items-center mb-8">
            <span className="text-xl font-bold text-[#0B1220]" style={{ fontFamily: "'Playfair Display', serif" }}>
              ÁGORA <span className="text-[#C9A24D]">HIPOTECAS</span>
            </span>
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close">
              <X size={28} className="text-[#0B1220]" />
            </button>
          </div>
          <div className="flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <a 
                key={link.name}
                href={link.href}
                className="text-lg text-[#0B1220] hover:text-[#C9A24D] transition-colors py-2"
                onClick={() => setMobileOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <Button 
              variant="outline"
              className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 rounded-full px-6 font-semibold mt-4"
              onClick={() => {
                setMobileOpen(false);
                onOpenAsesoramiento();
              }}
            >
              Asesoramiento personalizado
            </Button>
            <Button 
              className="bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] rounded-full px-6 font-semibold"
              onClick={() => {
                setMobileOpen(false);
                document.getElementById('simulador').scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Simular hipoteca
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

// Hero Section - Premium Full-Width Background
const HeroSection = ({ onOpenAsesoramiento }) => {
  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden" 
      data-testid="hero-section"
    >
      {/* Full-width Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_simulador-agora/artifacts/vowyzmu2_foto-19.jpg"
          alt="Despacho ÁGORA HIPOTECAS"
          className="w-full h-full object-cover object-center"
          data-testid="hero-image"
        />
        {/* Premium Dark Overlay - 35% opacity */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1220]/90 via-[#0B1220]/70 to-[#0B1220]/40" />
        {/* Subtle gold accent gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#C9A24D]/5" />
      </div>

      {/* Content Container */}
      <div className="container-agora relative z-10 py-32 lg:py-40">
        <div className="max-w-2xl">
          {/* Text Content - Left Aligned */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1 
              variants={staggerItem}
              className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="hero-title"
            >
              DEFENDEMOS TUS{' '}
              <span className="text-[#C9A24D]">INTERESES</span>
            </motion.h1>

            <motion.p 
              variants={staggerItem}
              className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed max-w-lg" 
              data-testid="hero-subtitle"
            >
              Te ayudamos a tomar la decisión financiera más importante de tu vida con el asesoramiento que mereces.
            </motion.p>
            
            {/* SOMOS Section - Premium Glass Card */}
            <motion.div 
              variants={staggerItem}
              className="mb-8 bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
            >
              <p className="text-xs font-semibold text-[#C9A24D] tracking-[0.2em] mb-4">SOMOS</p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-white">
                  <span className="w-1.5 h-1.5 bg-[#C9A24D] rounded-full"></span>
                  <span className="font-medium text-sm md:text-base">Asesores Estratégicos</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <span className="w-1.5 h-1.5 bg-[#C9A24D] rounded-full"></span>
                  <span className="font-medium text-sm md:text-base">Protectores Financieros</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <span className="w-1.5 h-1.5 bg-[#C9A24D] rounded-full"></span>
                  <span className="font-medium text-sm md:text-base">Guías del proceso completo de compra</span>
                </li>
              </ul>
            </motion.div>

            {/* CTAs */}
            <motion.div variants={staggerItem} className="flex flex-col sm:flex-row gap-4 mb-6">
              <Button 
                className="bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                data-testid="hero-cta-simular"
                onClick={() => document.getElementById('simulador').scrollIntoView({ behavior: 'smooth' })}
              >
                Simular Hipoteca
              </Button>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-white/50 text-white hover:bg-white hover:text-[#0B1220] rounded-full px-8 py-6 text-lg font-semibold hover:-translate-y-0.5 transition-all duration-300 backdrop-blur-sm"
                  data-testid="hero-cta-whatsapp"
                >
                  <ChatCircleDots size={24} weight="fill" className="mr-2" />
                  Hablar con un Asesor
                </Button>
              </a>
            </motion.div>

            {/* Trust Badge + Consulta gratuita - Inline flex */}
            <motion.div 
              variants={staggerItem}
              className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
            >
              <p className="text-sm text-white/70 flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-[#10B981]" />
                Primera consulta gratuita y sin compromiso
              </p>
              
              {/* Trust Badge - Premium Dark/Gold inline */}
              <div className="inline-flex items-center gap-3 bg-[#0B1220]/80 backdrop-blur-md rounded-xl px-4 py-2.5 border border-[#C9A24D]/40">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} weight="fill" className="text-[#C9A24D]" />
                  ))}
                </div>
                <div className="border-l border-[#C9A24D]/30 pl-3">
                  <p className="font-bold text-[#C9A24D] text-sm leading-tight">5.0/5</p>
                  <p className="text-xs text-white/60">+100 clientes</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
      >
        <span className="text-white/50 text-xs tracking-widest uppercase">Descubre más</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-1.5 bg-[#C9A24D] rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const reasons = [
    {
      icon: Bank,
      title: "Independientes de los bancos",
      description: "Trabajamos con múltiples entidades para encontrar la mejor opción para tu perfil."
    },
    {
      icon: Users,
      title: "Acompañamiento completo",
      description: "Asesoramiento integral durante todo el proceso de compra y financiación."
    },
    {
      icon: TrendUp,
      title: "Ahorro de tiempo y dinero",
      description: "Negociamos por ti para conseguir mejores condiciones."
    }
  ];

  return (
    <section className="py-16 bg-white" data-testid="why-choose-section">
      <div className="container-agora">
        <motion.div 
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <h2 className="section-title mb-4" data-testid="why-choose-title">¿Por qué elegir Ágora Hipotecas?</h2>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-3 gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {reasons.map((reason, index) => (
            <motion.div
              key={reason.title}
              className="text-center p-6 rounded-2xl hover:bg-[#FAF7F2] transition-all duration-500 group"
              variants={staggerItem}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              data-testid={`why-choose-card-${index}`}
            >
              <div className="w-16 h-16 bg-[#FAF7F2] group-hover:bg-white rounded-2xl flex items-center justify-center mx-auto mb-5 transition-all duration-300 group-hover:shadow-lg">
                <reason.icon size={32} className="text-[#C9A24D]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1220] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                {reason.title}
              </h3>
              <p className="text-[#6B7280]">{reason.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Trust Section
const TrustSection = () => {
  return (
    <section className="py-16 bg-white" data-testid="trust-section">
      <div className="container-agora">
        <motion.div 
          className="grid md:grid-cols-3 gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {/* Stat 1 */}
          <motion.div 
            className="trust-badge text-center group cursor-default"
            variants={staggerItem}
            whileHover={{ y: -6, transition: { duration: 0.3 } }}
          >
            <div className="flex justify-center mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={24} weight="fill" className="text-[#C9A24D]" />
              ))}
            </div>
            <p className="stat-number" data-testid="stat-rating">5.0/5</p>
            <p className="text-[#6B7280]">Valoración media de clientes</p>
          </motion.div>

          {/* Stat 2 */}
          <motion.div 
            className="trust-badge text-center group cursor-default"
            variants={staggerItem}
            whileHover={{ y: -6, transition: { duration: 0.3 } }}
          >
            <Users size={40} className="text-[#C9A24D] mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
            <p className="stat-number" data-testid="stat-operations">+500</p>
            <p className="text-[#6B7280]">Operaciones gestionadas</p>
          </motion.div>

          {/* Stat 3 */}
          <motion.div 
            className="trust-badge text-center group cursor-default"
            variants={staggerItem}
            whileHover={{ y: -6, transition: { duration: 0.3 } }}
          >
            <Bank size={40} className="text-[#C9A24D] mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
            <p className="stat-number" data-testid="stat-banks">+20</p>
            <p className="text-[#6B7280]">Bancos colaboradores</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Banks Carousel Section
const BanksCarouselSection = () => {
  return (
    <section className="py-20 bg-white overflow-hidden" data-testid="banks-carousel-section">
      <div className="container-agora mb-12">
        <h3 
          className="text-center text-xl md:text-2xl text-[#0B1220] font-semibold max-w-4xl mx-auto leading-relaxed"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          COLABORAMOS CON TODAS LAS ENTIDADES PARA CONSEGUIR LAS <span className="text-[#C9A24D]">MEJORES CONDICIONES</span> PARA TU PERFIL
        </h3>
      </div>
      <div className="relative">
        <div className="banks-carousel">
          <div className="banks-track">
            {[...BANKS, ...BANKS].map((bank, index) => (
              <div 
                key={`${bank.name}-${index}`}
                className="bank-logo-item group"
              >
                <div className="w-72 h-40 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-105 bg-white p-4 shadow-sm hover:shadow-lg border border-gray-100">
                  <img 
                    src={bank.logo} 
                    alt={bank.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="servicios" className="py-20 lg:py-28 bg-[#FAF7F2]" data-testid="services-section">
      <div className="container-agora">
        <motion.div 
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <h2 className="section-title mb-4" data-testid="services-title">Nuestros servicios</h2>
          <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
            Sea cual sea tu situación, tenemos la solución perfecta para ti
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-3 gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {SERVICES.map((service, index) => (
            <motion.div
              key={service.title}
              className="service-card group"
              variants={staggerItem}
              whileHover={{ y: -12, transition: { duration: 0.4 } }}
              data-testid={`service-card-${index}`}
            >
              <div className="h-48 overflow-hidden">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-[#FAF7F2] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#C9A24D]/10 transition-colors duration-300">
                  <service.icon size={28} className="text-[#C9A24D]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0B1220] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {service.title}
                </h3>
                <p className="text-[#6B7280] mb-6">{service.description}</p>
                <Button 
                  variant="outline"
                  className="w-full border-[#0B1220] text-[#0B1220] hover:bg-[#0B1220] hover:text-white rounded-full transition-all duration-300 hover:shadow-lg"
                  data-testid={`service-cta-${index}`}
                  onClick={() => document.getElementById('contacto').scrollIntoView({ behavior: 'smooth' })}
                >
                  Quiero mi estudio gratuito
                </Button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  return (
    <section id="proceso" className="py-20 lg:py-28 bg-white" data-testid="process-section">
      <div className="container-agora">
        <motion.div 
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <h2 className="section-title mb-4" data-testid="process-title">Un proceso simple y transparente</h2>
          <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
            Te acompañamos en cada paso del camino
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-3 gap-8 lg:gap-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {PROCESS_STEPS.map((step, index) => (
            <motion.div
              key={step.number}
              className="process-step text-center group"
              variants={staggerItem}
              data-testid={`process-step-${index}`}
            >
              <motion.div 
                className="w-16 h-16 bg-[#0B1220] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A24D] transition-colors duration-500"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <step.icon size={32} className="text-[#C9A24D] group-hover:text-[#0B1220] transition-colors duration-500" />
              </motion.div>
              <span className="text-[#C9A24D] font-bold text-sm tracking-widest">{step.number}</span>
              <h3 className="text-xl font-semibold text-[#0B1220] mt-2 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                {step.title}
              </h3>
              <p className="text-[#6B7280]">{step.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div 
          className="text-center mt-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <Button 
            className="bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            data-testid="process-cta"
            onClick={() => document.getElementById('contacto').scrollIntoView({ behavior: 'smooth' })}
          >
            Quiero mi estudio gratuito
          </Button>
          <p className="text-sm text-[#6B7280] mt-3">Respuesta en menos de 24h.</p>
        </motion.div>
      </div>
    </section>
  );
};

// Simulator Section
const SimulatorSection = () => {
  const [propertyPrice, setPropertyPrice] = useState(200000);
  const [downPayment, setDownPayment] = useState(40000);
  const [age, setAge] = useState([35]);
  const [years, setYears] = useState([30]);
  const [interestRate, setInterestRate] = useState([2.5]);
  const [location, setLocation] = useState("cataluna");
  const [purchaseType, setPurchaseType] = useState("segunda-mano");

  // Tax rates by region (approximate)
  const taxRates = {
    "cataluna": 0.115,
    "madrid": 0.10,
    "andalucia": 0.10,
    "valencia": 0.10,
    "pais-vasco": 0.07,
    "galicia": 0.10,
    "otras": 0.10
  };

  // Fixed interest rates for comparison
  // TIN Mercado (sin broker): 2.75%
  // TIN Ágora Hipotecas (con broker): 2.00%
  const MARKET_RATE = 2.75;  // TIN Mercado
  const AGORA_RATE = 2.00;   // TIN Ágora Hipotecas
  const RATE_ADVANTAGE = MARKET_RATE - AGORA_RATE; // 0.75% de ahorro

  const mortgageAmount = Math.max(0, propertyPrice - downPayment);
  const financingPercentage = propertyPrice > 0 ? Math.round((mortgageAmount / propertyPrice) * 100) : 0;
  const taxesAndFees = Math.round(propertyPrice * taxRates[location]);
  const totalPropertyCost = propertyPrice + taxesAndFees;
  const maxYears = Math.min(40, 75 - age[0]);

  const calculateMonthlyPayment = (principal, rate, numYears) => {
    const monthlyRate = rate / 100 / 12;
    const numberOfPayments = numYears * 12;
    
    if (principal <= 0 || monthlyRate <= 0) return 0;
    
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                   (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    return Math.round(payment);
  };

  const actualYears = Math.min(years[0], maxYears);
  const monthlyPaymentNoBroker = calculateMonthlyPayment(mortgageAmount, MARKET_RATE, actualYears);
  const monthlyPaymentWithBroker = calculateMonthlyPayment(mortgageAmount, AGORA_RATE, actualYears);
  const monthlySavings = monthlyPaymentNoBroker - monthlyPaymentWithBroker;
  const totalSavings = monthlySavings * actualYears * 12;
  
  const totalInterest = Math.round((monthlyPaymentWithBroker * actualYears * 12) - mortgageAmount);
  const totalOperationCost = mortgageAmount + taxesAndFees + totalInterest;

  // Handle manual input for price
  const handlePriceChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const numValue = parseInt(value) || 0;
    setPropertyPrice(Math.min(Math.max(numValue, 0), 2000000));
  };

  // Handle manual input for down payment
  const handleDownPaymentChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const numValue = parseInt(value) || 0;
    setDownPayment(Math.min(Math.max(numValue, 0), propertyPrice));
  };

  return (
    <section id="simulador" className="py-20 lg:py-28 bg-[#0B1220]" data-testid="simulator-section">
      <div className="container-agora">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <h2 
            className="text-4xl lg:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="simulator-title"
          >
            Simulador <span className="text-[#C9A24D]">Hipoteca</span>
          </h2>
          <p className="text-[#9CA3AF] max-w-2xl mx-auto">
            Calcula tu cuota y descubre cuánto puedes ahorrar con nuestro asesoramiento profesional.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <div className="space-y-6">
              {/* Property Price - Manual Input + Slider */}
              <div className="bg-[#1a2a3f] border border-white/10 rounded-2xl p-5">
                <label className="block text-white font-medium mb-3">Precio de compra</label>
                <div className="relative mb-4">
                  <input
                    type="text"
                    value={propertyPrice.toLocaleString('es-ES')}
                    onChange={handlePriceChange}
                    className="w-full bg-[#0B1220] border border-[#C9A24D]/30 rounded-xl px-4 py-4 text-2xl font-bold text-[#C9A24D] text-right pr-12 focus:outline-none focus:border-[#C9A24D] transition-colors"
                    data-testid="input-property-price"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#C9A24D] font-bold text-xl">€</span>
                </div>
                <Slider
                  value={[propertyPrice]}
                  onValueChange={(val) => setPropertyPrice(val[0])}
                  min={50000}
                  max={1500000}
                  step={5000}
                  className="slider-gold"
                />
                <div className="flex justify-between mt-2 text-sm text-[#6B7280]">
                  <span>50.000 €</span>
                  <span>1.500.000 €</span>
                </div>
              </div>

              {/* Down Payment - Manual Input + Slider */}
              <div className="bg-[#1a2a3f] border border-white/10 rounded-2xl p-5">
                <label className="block text-white font-medium mb-3">Entrada (ahorros disponibles)</label>
                <div className="relative mb-4">
                  <input
                    type="text"
                    value={downPayment.toLocaleString('es-ES')}
                    onChange={handleDownPaymentChange}
                    className="w-full bg-[#0B1220] border border-[#C9A24D]/30 rounded-xl px-4 py-4 text-2xl font-bold text-[#C9A24D] text-right pr-12 focus:outline-none focus:border-[#C9A24D] transition-colors"
                    data-testid="input-down-payment"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#C9A24D] font-bold text-xl">€</span>
                </div>
                <Slider
                  value={[downPayment]}
                  onValueChange={(val) => setDownPayment(val[0])}
                  min={0}
                  max={Math.min(propertyPrice * 0.9, 800000)}
                  step={1000}
                  className="slider-gold"
                />
                <div className="flex justify-between mt-2 text-sm text-[#6B7280]">
                  <span>0 €</span>
                  <span>{Math.min(propertyPrice * 0.9, 800000).toLocaleString('es-ES')} €</span>
                </div>
                <p className="text-xs text-[#6B7280] mt-2">
                  Financiación: <span className="text-[#C9A24D] font-semibold">{financingPercentage}%</span> del precio
                </p>
              </div>

              {/* Age + Years Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Age */}
                <div className="bg-[#1a2a3f] border border-white/10 rounded-2xl p-4">
                  <div className="flex justify-between mb-2">
                    <label className="text-white font-medium text-sm">Tu edad</label>
                    <span className="text-[#C9A24D] font-bold">{age[0]} años</span>
                  </div>
                  <Slider
                    value={age}
                    onValueChange={setAge}
                    min={18}
                    max={65}
                    step={1}
                    className="slider-gold"
                  />
                </div>

                {/* Years */}
                <div className="bg-[#1a2a3f] border border-white/10 rounded-2xl p-4">
                  <div className="flex justify-between mb-2">
                    <label className="text-white font-medium text-sm">Plazo</label>
                    <span className="text-[#C9A24D] font-bold">{actualYears} años</span>
                  </div>
                  <Slider
                    value={[Math.min(years[0], maxYears)]}
                    onValueChange={setYears}
                    min={5}
                    max={maxYears}
                    step={1}
                    className="slider-gold"
                  />
                </div>
              </div>

              {/* Interest Rate */}
              <div className="bg-[#1a2a3f] border border-white/10 rounded-2xl p-5">
                <div className="flex justify-between mb-3">
                  <label className="text-white font-medium">Tipo de interés de mercado</label>
                  <span className="text-[#C9A24D] font-bold">{interestRate[0].toFixed(2)} %</span>
                </div>
                <Slider
                  value={interestRate}
                  onValueChange={setInterestRate}
                  min={1.0}
                  max={6}
                  step={0.1}
                  className="slider-gold"
                />
                <div className="flex justify-between mt-2 text-sm text-[#6B7280]">
                  <span>1.0 %</span>
                  <span>6.0 %</span>
                </div>
              </div>

              {/* Location + Purchase Type Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2 text-sm">Ubicación</label>
                  <Select value={location} onValueChange={setLocation}>
                    <SelectTrigger className="w-full bg-[#1a2a3f] border border-white/10 text-white rounded-xl py-5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cataluna">Cataluña</SelectItem>
                      <SelectItem value="madrid">Madrid</SelectItem>
                      <SelectItem value="andalucia">Andalucía</SelectItem>
                      <SelectItem value="valencia">C. Valenciana</SelectItem>
                      <SelectItem value="pais-vasco">País Vasco</SelectItem>
                      <SelectItem value="galicia">Galicia</SelectItem>
                      <SelectItem value="otras">Otras</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-white font-medium mb-2 text-sm">Tipo de compra</label>
                  <div className="flex gap-0 rounded-xl overflow-hidden border border-white/10">
                    <button
                      onClick={() => setPurchaseType("nuevo")}
                      className={`flex-1 py-3.5 text-center font-medium text-sm transition-all ${
                        purchaseType === "nuevo" 
                          ? "bg-[#C9A24D] text-[#0B1220]" 
                          : "bg-[#1a2a3f] text-white hover:bg-[#2a3a4f]"
                      }`}
                    >
                      Nuevo
                    </button>
                    <button
                      onClick={() => setPurchaseType("segunda-mano")}
                      className={`flex-1 py-3.5 text-center font-medium text-sm transition-all ${
                        purchaseType === "segunda-mano" 
                          ? "bg-[#C9A24D] text-[#0B1220]" 
                          : "bg-[#1a2a3f] text-white hover:bg-[#2a3a4f]"
                      }`}
                    >
                      Segunda mano
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Results */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            {/* Broker vs No Broker Comparison */}
            <div className="bg-gradient-to-br from-[#C9A24D]/20 to-[#C9A24D]/5 border border-[#C9A24D]/30 rounded-2xl p-6 mb-6" data-testid="broker-comparison">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <TrendUp size={20} className="text-[#C9A24D]" />
                Ahorro con ÁGORA HIPOTECAS
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Without Broker */}
                <div className="bg-[#0B1220]/50 rounded-xl p-4 border border-white/5">
                  <p className="text-[#6B7280] text-xs uppercase tracking-wide mb-1">Sin broker</p>
                  <p className="text-white text-xl font-bold">{monthlyPaymentNoBroker.toLocaleString('es-ES')} €<span className="text-sm font-normal text-[#6B7280]">/mes</span></p>
                  <p className="text-[#6B7280] text-xs mt-1">TIN: {MARKET_RATE.toFixed(2)}%</p>
                </div>
                
                {/* With Broker */}
                <div className="bg-[#0B1220]/50 rounded-xl p-4 border border-[#C9A24D]/30">
                  <p className="text-[#C9A24D] text-xs uppercase tracking-wide mb-1">Con ÁGORA</p>
                  <p className="text-[#C9A24D] text-xl font-bold">{monthlyPaymentWithBroker.toLocaleString('es-ES')} €<span className="text-sm font-normal text-[#C9A24D]/70">/mes</span></p>
                  <p className="text-[#C9A24D]/70 text-xs mt-1">TIN: {AGORA_RATE.toFixed(2)}%</p>
                </div>
              </div>

              {/* Savings Highlight */}
              <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#10B981] text-sm font-medium">Tu ahorro mensual</p>
                    <p className="text-[#10B981] text-2xl font-bold">{monthlySavings.toLocaleString('es-ES')} €</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#10B981] text-sm font-medium">Ahorro total estimado</p>
                    <p className="text-[#10B981] text-2xl font-bold">{totalSavings.toLocaleString('es-ES')} €</p>
                  </div>
                </div>
                <p className="text-[#10B981]/70 text-xs mt-2">*Comparativa entre TIN mercado ({MARKET_RATE}%) y TIN negociado por ÁGORA ({AGORA_RATE}%)</p>
              </div>
            </div>

            {/* Monthly Payment Card */}
            <div className="bg-[#1a2a3f] border border-white/10 rounded-2xl p-6 mb-6">
              <p className="text-[#9CA3AF] mb-2">Tu cuota mensual con ÁGORA</p>
              <p 
                className="text-5xl lg:text-6xl font-bold text-[#C9A24D] mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="monthly-payment"
              >
                {monthlyPaymentWithBroker.toLocaleString('es-ES')} €
              </p>
              <p className="text-[#9CA3AF] text-sm">Importe a financiar: {mortgageAmount.toLocaleString('es-ES')} €</p>
            </div>

            {/* Details Card */}
            <div className="bg-[#1a2a3f] border border-white/10 rounded-2xl p-5 mb-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-[#9CA3AF] text-sm">Precio vivienda</span>
                  <span className="font-semibold text-white">{propertyPrice.toLocaleString('es-ES')} €</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-[#9CA3AF] text-sm">Impuestos y gastos</span>
                  <span className="font-semibold text-white">{taxesAndFees.toLocaleString('es-ES')} €</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-[#9CA3AF] text-sm">Intereses hipotecarios</span>
                  <span className="font-semibold text-[#C9A24D]">{totalInterest.toLocaleString('es-ES')} €</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-[#9CA3AF] text-sm">Coste total operación</span>
                  <span className="font-semibold text-[#C9A24D]">{totalOperationCost.toLocaleString('es-ES')} €</span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Button 
              className="w-full bg-gradient-to-r from-[#C9A24D] to-[#D4AF5A] hover:from-[#D4AF5A] hover:to-[#E0BC6A] text-[#0B1220] rounded-xl py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              data-testid="simulator-cta"
              onClick={() => document.getElementById('contacto').scrollIntoView({ behavior: 'smooth' })}
            >
              <CheckCircle size={22} className="mr-2" />
              Solicitar estudio personalizado
            </Button>
            <p className="text-center text-[#6B7280] text-xs mt-3">Sin compromiso. Respuesta en menos de 24h.</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Reviews Section with Auto Carousel
const ReviewsSection = () => {
  const [isPaused, setIsPaused] = useState(false);
  
  // URL de Google Business Profile - Ficha oficial de ÁGORA HIPOTECAS
  const GOOGLE_REVIEWS_URL = "https://www.google.es/maps/place/%C3%81GORA+HIPOTECAS/@41.3082985,1.9990429,17z/data=!3m1!4b1!4m6!3m5!1s0x12a49da084a70595:0xe9e2b33fa00bd4d0!8m2!3d41.3082985!4d2.0016178!16s%2Fg%2F11z0x4l9dn";

  return (
    <section id="resenas" className="py-20 lg:py-28 bg-white overflow-hidden" data-testid="reviews-section">
      <div className="container-agora">
        <motion.div 
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          {/* Google Reviews Badge */}
          <div className="inline-flex items-center gap-4 bg-white border border-gray-200 rounded-2xl px-6 py-4 shadow-lg mb-8">
            {/* Google Logo */}
            <svg className="w-8 h-8" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-[#0B1220]">5.0</span>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} weight="fill" className="text-[#FBBC05]" />
                  ))}
                </div>
              </div>
              <span className="text-sm text-[#6B7280]">Basado en 33 reseñas de Google</span>
            </div>
          </div>

          <h2 className="section-title mb-4" data-testid="reviews-title">Lo que dicen nuestros clientes</h2>
          <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
            Reseñas verificadas de Google Business Profile.
          </p>
        </motion.div>
      </div>

      {/* Reviews Carousel */}
      <div 
        className="reviews-carousel-container"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className={`reviews-carousel ${isPaused ? 'paused' : ''}`}>
          <div className="reviews-track">
            {[...REVIEWS, ...REVIEWS].map((review, index) => (
              <div
                key={`${review.name}-${index}`}
                className="review-carousel-card"
                data-testid={`review-card-${index}`}
              >
                {/* Google Badge + Stars */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={16} weight="fill" className="text-[#FBBC05]" />
                    ))}
                  </div>
                  {review.verified && (
                    <div className="flex items-center gap-1 text-xs text-[#6B7280]">
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      <span>Google</span>
                    </div>
                  )}
                </div>
                
                {/* Quote */}
                <p className="text-[#0B1220] mb-4 leading-relaxed text-sm">&ldquo;{review.text}&rdquo;</p>
                
                {/* Author */}
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0B1220] rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">{review.avatar}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-[#0B1220] text-sm">{review.name}</p>
                      <p className="text-xs text-[#6B7280]">{review.location}</p>
                    </div>
                  </div>
                  {review.date && (
                    <span className="text-xs text-[#9CA3AF]">{review.date}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ver todas las reseñas en Google */}
      <div className="container-agora mt-10">
        <motion.div 
          className="text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <a 
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#0B1220] hover:text-[#C9A24D] font-medium transition-colors group"
            data-testid="google-reviews-link"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Ver todas las reseñas en Google
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  return (
    <section id="equipo" className="py-20 lg:py-28 bg-[#FAF7F2]" data-testid="team-section">
      <div className="container-agora">
        <motion.div 
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <h2 className="section-title mb-4" data-testid="team-title">Conoce al equipo</h2>
          <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
            Más de 10 años ayudando a familias a conseguir su vivienda.
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {TEAM.map((member, index) => (
            <motion.div
              key={member.name}
              className="team-card-premium group"
              variants={staggerItem}
              data-testid={`team-card-${index}`}
            >
              {/* Image Container - Fixed aspect ratio for perfect alignment */}
              <div className="relative aspect-[3/4] overflow-hidden bg-[#0B1220]">
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                  style={{
                    objectPosition: member.objectPosition || 'center 15%',
                    filter: member.filterStyle || 'brightness(1.0) contrast(1.02) saturate(1.0)',
                    transform: `scale(${member.scale || 1.0})`
                  }}
                />
                {/* Uniform gradient overlay for visual consistency across all photos */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220]/40 via-transparent to-[#0B1220]/10" />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-[#C9A24D]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              
              {/* Content - Fixed heights for perfect alignment */}
              <div className="p-5 text-center bg-white">
                <h3 
                  className="text-xl font-semibold text-[#0B1220] mb-1 h-7"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {member.name}
                </h3>
                <p className="text-[#C9A24D] font-medium text-sm mb-3 h-5">{member.role}</p>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-5 h-[72px] overflow-hidden">{member.bio}</p>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="block">
                  <Button 
                    className="w-full bg-[#0B1220] hover:bg-[#1a2a3f] text-white rounded-full py-5 text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                    data-testid={`team-cta-${index}`}
                  >
                    <ChatCircleDots size={18} className="mr-2" />
                    Contactar
                  </Button>
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    mortgageType: "",
    message: ""
  });
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptPrivacy) {
      toast.error("Debes aceptar la política de privacidad para continuar.");
      return;
    }
    
    setIsSubmitting(true);
    console.log("📤 Submitting contact form...");
    
    try {
      // Use relative URL to ensure it works on any domain
      const API_URL = '';
      console.log("📡 API URL: Using relative path (same domain)");
      
      const payload = {
        nombre: formData.name,
        email: formData.email,
        telefono: formData.phone,
        tipo_hipoteca: formData.mortgageType,
        mensaje: formData.message,
        origen: "Formulario Contacto Web",
        acepta_privacidad: acceptPrivacy,
      };
      console.log("📦 Payload:", payload);
      
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      console.log("📥 Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ HubSpot error:", errorText);
        throw new Error('Error al enviar el formulario');
      }
      
      const data = await response.json();
      console.log("✅ HubSpot success:", data);
      
      toast.success("¡Gracias por tu mensaje! Te contactaremos pronto.", {
        description: "Revisaremos tu solicitud y te responderemos en menos de 24 horas.",
      });
      setFormData({ name: "", email: "", phone: "", mortgageType: "", message: "" });
      setAcceptPrivacy(false);
    } catch (error) {
      console.error('❌ Form submission error:', error);
      toast.error("Hubo un error al enviar el formulario. Por favor, inténtalo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contacto" className="py-20 lg:py-28 bg-white" data-testid="contact-section">
      <div className="container-agora">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <h2 className="section-title mb-4" data-testid="contact-title">
              Empieza hoy tu estudio hipotecario
            </h2>
            <p className="text-lg text-[#6B7280] mb-8">
              Déjanos tus datos y un asesor te contactará en menos de 24h.
            </p>

            <motion.div 
              className="space-y-6"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div variants={staggerItem} className="flex items-start gap-4 group">
                <div className="w-12 h-12 bg-[#FAF7F2] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A24D]/10 transition-colors duration-300">
                  <MapPin size={24} className="text-[#C9A24D]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0B1220] mb-1">Ubicación</h4>
                  <p className="text-[#6B7280]">Barcelona, España</p>
                </div>
              </motion.div>

              <motion.div variants={staggerItem} className="flex items-start gap-4 group">
                <div className="w-12 h-12 bg-[#FAF7F2] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A24D]/10 transition-colors duration-300">
                  <Phone size={24} className="text-[#C9A24D]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0B1220] mb-1">Teléfono</h4>
                  <p className="text-[#6B7280]">+34 676 280 792</p>
                </div>
              </motion.div>

              <motion.div variants={staggerItem} className="flex items-start gap-4 group">
                <div className="w-12 h-12 bg-[#FAF7F2] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A24D]/10 transition-colors duration-300">
                  <Envelope size={24} className="text-[#C9A24D]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0B1220] mb-1">Email</h4>
                  <p className="text-[#6B7280]">agorahipotecas@gmail.com</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInScale}
          >
            <form onSubmit={handleSubmit} className="contact-form p-8" data-testid="contact-form">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#0B1220] mb-2">Nombre completo</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    required
                    className="rounded-xl border-gray-200 focus:border-[#C9A24D]"
                    data-testid="input-name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#0B1220] mb-2">Email</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      required
                      className="rounded-xl border-gray-200 focus:border-[#C9A24D]"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1220] mb-2">Teléfono</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+34 676 280 792"
                      required
                      className="rounded-xl border-gray-200 focus:border-[#C9A24D]"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0B1220] mb-2">Tipo de hipoteca</label>
                  <Select 
                    value={formData.mortgageType} 
                    onValueChange={(value) => setFormData({...formData, mortgageType: value})}
                  >
                    <SelectTrigger className="rounded-xl border-gray-200" data-testid="select-mortgage-type">
                      <SelectValue placeholder="Selecciona una opción" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="primera-vivienda">Primera vivienda</SelectItem>
                      <SelectItem value="mejora-hipoteca">Mejora de hipoteca</SelectItem>
                      <SelectItem value="inversion">Inversión inmobiliaria</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0B1220] mb-2">Mensaje (opcional)</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Cuéntanos más sobre tu situación..."
                    rows={4}
                    className="rounded-xl border-gray-200 focus:border-[#C9A24D] resize-none"
                    data-testid="input-message"
                  />
                </div>

                {/* Privacy Checkbox */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy"
                    checked={acceptPrivacy}
                    onCheckedChange={setAcceptPrivacy}
                    className="mt-1 border-gray-300 data-[state=checked]:bg-[#C9A24D] data-[state=checked]:border-[#C9A24D]"
                    data-testid="checkbox-privacy"
                  />
                  <label htmlFor="privacy" className="text-sm text-[#6B7280] leading-relaxed cursor-pointer">
                    He leído y acepto la{' '}
                    <Link to="/politica-privacidad" className="text-[#C9A24D] hover:underline" target="_blank">
                      Política de Privacidad
                    </Link>
                    {' '}y consiento el tratamiento de mis datos para gestionar mi solicitud.
                  </label>
                </div>

                <Button 
                  type="submit"
                  disabled={!acceptPrivacy || isSubmitting}
                  className="w-full bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] rounded-full py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={22} className="mr-2" />
                      Quiero mi estudio gratuito
                    </>
                  )}
                </Button>
                <p className="text-center text-sm text-[#6B7280] mt-3">
                  Consulta gratuita y sin compromiso.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// TikTok Section
const TikTokSection = () => {
  const TIKTOK_PROFILE = "https://www.tiktok.com/@agorahipotecas";
  
  // TikTok videos con URLs directos y thumbnails permanentes
  const tiktokVideos = [
    {
      id: 1,
      title: "Consejos para tu primera hipoteca",
      thumbnail: "/tiktok/video1.jpg",
      views: "12.5K",
      url: "https://www.tiktok.com/@agorahipotecas/video/7644649881897831702"
    },
    {
      id: 2,
      title: "Errores que evitar al pedir una hipoteca",
      thumbnail: "/tiktok/video2.jpg",
      views: "8.3K",
      url: "https://www.tiktok.com/@agorahipotecas/video/7643393251919056150"
    },
    {
      id: 3,
      title: "¿Cuánto ahorras con un broker?",
      thumbnail: "/tiktok/video3.jpg",
      views: "15.2K",
      url: "https://www.tiktok.com/@agorahipotecas/video/7642037106360978710"
    },
    {
      id: 4,
      title: "El proceso de compra explicado",
      thumbnail: "/tiktok/video4.jpg",
      views: "9.7K",
      url: "https://www.tiktok.com/@agorahipotecas/video/7634283721486519574"
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-[#0B1220] overflow-hidden" data-testid="tiktok-section">
      <div className="container-agora">
        <motion.div 
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          {/* TikTok Logo */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <svg 
              className="w-8 h-8" 
              viewBox="0 0 24 24" 
              fill="none"
            >
              <path 
                d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" 
                fill="#C9A24D"
              />
            </svg>
            <h2 
              className="text-3xl lg:text-4xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="tiktok-title"
            >
              Síguenos en <span className="text-[#C9A24D]">TikTok</span>
            </h2>
          </div>
          <p className="text-[#9CA3AF] max-w-2xl mx-auto">
            Consejos prácticos, trucos y todo lo que necesitas saber sobre hipotecas en formato rápido y entretenido.
          </p>
        </motion.div>

        {/* Video Grid */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {tiktokVideos.map((video, index) => (
            <motion.a
              key={video.id}
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative rounded-2xl overflow-hidden aspect-[9/16] bg-[#1a2a3f]"
              variants={staggerItem}
              whileHover={{ scale: 1.03 }}
              data-testid={`tiktok-video-${index}`}
            >
              <img 
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
              
              {/* Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-[#C9A24D] group-hover:scale-110 transition-all duration-300">
                  <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
              
              {/* Video Info */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-white text-sm font-medium line-clamp-2 mb-1">{video.title}</p>
                <p className="text-[#C9A24D] text-xs flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                  {video.views}
                </p>
              </div>
            </motion.a>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div 
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <a href={TIKTOK_PROFILE} target="_blank" rel="noopener noreferrer">
            <Button 
              className="bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] rounded-full px-8 py-6 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
              data-testid="tiktok-follow-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
              </svg>
              Seguir en TikTok
            </Button>
          </a>
          <a href={TIKTOK_PROFILE} target="_blank" rel="noopener noreferrer">
            <Button 
              variant="outline"
              className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 font-semibold hover:-translate-y-0.5 transition-all duration-300"
              data-testid="tiktok-watch-btn"
            >
              Ver más vídeos
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Colabora con Nosotros Section (B2B)
const ColaboraSection = () => {
  const [formData, setFormData] = useState({
    nombre: "",
    empresa: "",
    cif: "",
    tipoColaborador: "",
    email: "",
    telefono: "",
    hipotecasMes: "",
    notas: ""
  });
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptPrivacy) {
      toast.error("Debes aceptar la política de privacidad para continuar.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const payload = {
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono,
        tipo_hipoteca: `B2B - ${formData.tipoColaborador}`,
        mensaje: `Empresa: ${formData.empresa}\nCIF: ${formData.cif}\nTipo: ${formData.tipoColaborador}\nHipotecas/mes: ${formData.hipotecasMes}\nNotas: ${formData.notas}`,
        origen: "Formulario Colaboradores B2B",
        acepta_privacidad: acceptPrivacy,
      };
      
      const response = await fetch(`/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        throw new Error('Error al enviar el formulario');
      }
      
      toast.success("¡Gracias por tu interés en colaborar!", {
        description: "Nuestro equipo se pondrá en contacto contigo pronto.",
      });
      setFormData({ 
        nombre: "", empresa: "", cif: "", tipoColaborador: "", 
        email: "", telefono: "", hipotecasMes: "", notas: "" 
      });
      setAcceptPrivacy(false);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error("Hubo un error al enviar el formulario. Por favor, inténtalo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const colaboradorTypes = [
    { value: "inmobiliaria", label: "Inmobiliaria" },
    { value: "api", label: "API (Agente Propiedad Inmobiliaria)" },
    { value: "promotora", label: "Promotora" },
    { value: "abogado", label: "Abogado / Despacho jurídico" },
    { value: "gestor", label: "Gestoría / Asesoría" },
    { value: "otro", label: "Otro" }
  ];

  return (
    <section id="colabora" className="py-20 lg:py-28 bg-[#FAF7F2]" data-testid="colabora-section">
      <div className="container-agora">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <span className="text-[#C9A24D] font-semibold text-sm tracking-widest mb-4 block">PARTNERS</span>
            <h2 
              className="text-3xl lg:text-4xl font-bold text-[#0B1220] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="colabora-title"
            >
              Colabora con <span className="text-[#C9A24D]">nosotros</span>
            </h2>
            <p className="text-lg text-[#6B7280] mb-8">
              ¿Eres profesional del sector inmobiliario? Únete a nuestra red de colaboradores y ofrece a tus clientes el mejor servicio de asesoramiento hipotecario.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4 group">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow">
                  <Users size={24} className="text-[#C9A24D]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0B1220] mb-1">Red de profesionales</h4>
                  <p className="text-[#6B7280] text-sm">Forma parte de nuestra red de inmobiliarias, APIs y profesionales del sector.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow">
                  <TrendUp size={24} className="text-[#C9A24D]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0B1220] mb-1">Comisiones competitivas</h4>
                  <p className="text-[#6B7280] text-sm">Obtén comisiones por cada operación cerrada con tus referidos.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow">
                  <ShieldCheck size={24} className="text-[#C9A24D]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0B1220] mb-1">Servicio de confianza</h4>
                  <p className="text-[#6B7280] text-sm">Tus clientes recibirán el mismo trato premium que ofrecemos a todos.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInScale}
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100" data-testid="colabora-form">
              <h3 className="text-xl font-semibold text-[#0B1220] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Solicita información
              </h3>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0B1220] mb-2">Nombre completo *</label>
                    <Input
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                      required
                      className="rounded-xl border-gray-200 focus:border-[#C9A24D]"
                      data-testid="colabora-input-nombre"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1220] mb-2">Empresa *</label>
                    <Input
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      placeholder="Nombre de la empresa"
                      required
                      className="rounded-xl border-gray-200 focus:border-[#C9A24D]"
                      data-testid="colabora-input-empresa"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0B1220] mb-2">CIF</label>
                    <Input
                      name="cif"
                      value={formData.cif}
                      onChange={handleChange}
                      placeholder="B12345678"
                      className="rounded-xl border-gray-200 focus:border-[#C9A24D]"
                      data-testid="colabora-input-cif"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1220] mb-2">Tipo de colaborador *</label>
                    <Select 
                      value={formData.tipoColaborador} 
                      onValueChange={(value) => setFormData({...formData, tipoColaborador: value})}
                    >
                      <SelectTrigger className="rounded-xl border-gray-200" data-testid="colabora-select-tipo">
                        <SelectValue placeholder="Selecciona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {colaboradorTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0B1220] mb-2">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@empresa.com"
                      required
                      className="rounded-xl border-gray-200 focus:border-[#C9A24D]"
                      data-testid="colabora-input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1220] mb-2">Teléfono *</label>
                    <Input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      placeholder="+34 600 000 000"
                      required
                      className="rounded-xl border-gray-200 focus:border-[#C9A24D]"
                      data-testid="colabora-input-telefono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0B1220] mb-2">Hipotecas estimadas al mes</label>
                  <Select 
                    value={formData.hipotecasMes} 
                    onValueChange={(value) => setFormData({...formData, hipotecasMes: value})}
                  >
                    <SelectTrigger className="rounded-xl border-gray-200" data-testid="colabora-select-hipotecas">
                      <SelectValue placeholder="Selecciona un rango" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-5">1-5 hipotecas/mes</SelectItem>
                      <SelectItem value="6-15">6-15 hipotecas/mes</SelectItem>
                      <SelectItem value="16-30">16-30 hipotecas/mes</SelectItem>
                      <SelectItem value="30+">Más de 30 hipotecas/mes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0B1220] mb-2">Notas adicionales</label>
                  <Textarea
                    name="notas"
                    value={formData.notas}
                    onChange={handleChange}
                    placeholder="Cuéntanos más sobre tu empresa y cómo te gustaría colaborar..."
                    rows={3}
                    className="rounded-xl border-gray-200 focus:border-[#C9A24D] resize-none"
                    data-testid="colabora-input-notas"
                  />
                </div>

                {/* Privacy Checkbox */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy-colabora"
                    checked={acceptPrivacy}
                    onCheckedChange={setAcceptPrivacy}
                    className="mt-1 border-gray-300 data-[state=checked]:bg-[#C9A24D] data-[state=checked]:border-[#C9A24D]"
                    data-testid="colabora-checkbox-privacy"
                  />
                  <label htmlFor="privacy-colabora" className="text-sm text-[#6B7280] leading-relaxed cursor-pointer">
                    He leído y acepto la{' '}
                    <Link to="/politica-privacidad" className="text-[#C9A24D] hover:underline" target="_blank">
                      Política de Privacidad
                    </Link>
                    {' '}y consiento el tratamiento de mis datos.
                  </label>
                </div>

                <Button 
                  type="submit"
                  disabled={!acceptPrivacy || isSubmitting}
                  className="w-full bg-[#0B1220] hover:bg-[#1a2a3f] text-white rounded-full py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="colabora-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Users size={20} className="mr-2" />
                      Quiero ser colaborador
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer py-16" data-testid="footer">
      <div className="container-agora">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <span className="text-2xl font-bold text-white mb-4 block" style={{ fontFamily: "'Playfair Display', serif" }}>
              ÁGORA <span className="text-[#C9A24D]">HIPOTECAS</span>
            </span>
            <p className="text-[#9CA3AF] mb-6 max-w-md">
              Tu aliado para conseguir la mejor hipoteca. Te acompañamos de principio a fin para que solo te preocupes de disfrutar tu nuevo hogar.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://instagram.com/agorahipotecas" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#C9A24D] transition-all duration-300 hover:scale-110"
                data-testid="social-instagram"
              >
                <InstagramLogo size={20} className="text-white" />
              </a>
              <a 
                href="https://linkedin.com/company/agora-hipotecas" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#C9A24D] transition-all duration-300 hover:scale-110"
                data-testid="social-linkedin"
              >
                <LinkedinLogo size={20} className="text-white" />
              </a>
              <a 
                href={WHATSAPP_URL}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#25D366] transition-all duration-300 hover:scale-110"
                data-testid="social-whatsapp"
              >
                <WhatsappLogo size={20} className="text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Enlaces rápidos</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-[#9CA3AF] hover:text-[#C9A24D] transition-colors">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contacto</h4>
            <ul className="space-y-3 text-[#9CA3AF]">
              <li className="flex items-center gap-2">
                <MapPin size={18} className="text-[#C9A24D]" />
                Barcelona, España
              </li>
              <li className="flex items-center gap-2">
                <Phone size={18} className="text-[#C9A24D]" />
                +34 676 280 792
              </li>
              <li className="flex items-center gap-2">
                <Envelope size={18} className="text-[#C9A24D]" />
                agorahipotecas@gmail.com
              </li>
            </ul>
          </div>
        </div>

        {/* Institutional Trust - Regulatory */}
        <div className="border-t border-white/10 pt-8 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 text-center">
            <div className="flex items-center gap-4 group cursor-default transition-all duration-300 hover:scale-105">
              <div className="w-11 h-11 border border-[#C9A24D]/40 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:border-[#C9A24D] group-hover:bg-[#C9A24D]/10">
                <Bank size={22} weight="light" className="text-[#C9A24D]" />
              </div>
              <div className="text-left">
                <p className="text-xs text-[#6B7280] tracking-wide">Regulado por</p>
                <p className="text-sm text-[#9CA3AF] font-medium">Banca de España</p>
              </div>
            </div>
            
            <div className="hidden md:block w-px h-10 bg-white/10"></div>
            
            <div className="flex items-center gap-4 group cursor-default transition-all duration-300 hover:scale-105">
              <div className="w-11 h-11 border border-[#C9A24D]/40 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:border-[#C9A24D] group-hover:bg-[#C9A24D]/10">
                <FileText size={22} weight="light" className="text-[#C9A24D]" />
              </div>
              <div className="text-left">
                <p className="text-xs text-[#6B7280] tracking-wide">Cumplimiento</p>
                <p className="text-sm text-[#9CA3AF] font-medium">RGPD & LOPD</p>
              </div>
            </div>

            <div className="hidden md:block w-px h-10 bg-white/10"></div>

            <div className="flex items-center gap-4 group cursor-default transition-all duration-300 hover:scale-105">
              <div className="w-11 h-11 border border-[#C9A24D]/40 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:border-[#C9A24D] group-hover:bg-[#C9A24D]/10">
                <ShieldCheck size={22} weight="light" className="text-[#C9A24D]" />
              </div>
              <div className="text-left">
                <p className="text-xs text-[#6B7280] tracking-wide">Miembro de</p>
                <p className="text-sm text-[#9CA3AF] font-medium">ANPIFF</p>
              </div>
            </div>

            <div className="hidden md:block w-px h-10 bg-white/10"></div>

            <div className="flex items-center gap-4 group cursor-default transition-all duration-300 hover:scale-105">
              <div className="w-11 h-11 border border-[#C9A24D]/40 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:border-[#C9A24D] group-hover:bg-[#C9A24D]/10">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={8} weight="fill" className="text-[#C9A24D]" />
                  ))}
                </div>
              </div>
              <div className="text-left">
                <p className="text-xs text-[#6B7280] tracking-wide">Valoración</p>
                <p className="text-sm text-[#9CA3AF] font-medium">5.0 Google Reviews</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#6B7280] text-sm">
            © {new Date().getFullYear()} Ágora Hipotecas — Todos los derechos reservados.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/aviso-legal" className="text-[#6B7280] hover:text-[#C9A24D] transition-colors" data-testid="link-legal">
              Aviso Legal
            </Link>
            <Link to="/politica-privacidad" className="text-[#6B7280] hover:text-[#C9A24D] transition-colors" data-testid="link-privacy">
              Política de Privacidad
            </Link>
            <Link to="/politica-cookies" className="text-[#6B7280] hover:text-[#C9A24D] transition-colors" data-testid="link-cookies">
              Política de Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <a 
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-btn animate-pulse-gold"
      data-testid="whatsapp-floating-btn"
    >
      <WhatsappLogo size={32} weight="fill" className="text-white" />
    </a>
  );
};

// Cookie Banner Component
const COOKIE_CONSENT_KEY = 'agora_cookie_consent';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowConfig(false);
  };

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  };

  const rejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed bottom-0 left-0 right-0 z-[200] p-4 md:p-6"
        data-testid="cookie-banner"
      >
        <div className="max-w-4xl mx-auto bg-[#0B1220] rounded-2xl shadow-2xl overflow-hidden">
          {!showConfig ? (
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#C9A24D]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Cookie size={28} className="text-[#C9A24D]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white text-lg font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Utilizamos cookies
                  </h3>
                  <p className="text-[#9CA3AF] text-sm leading-relaxed mb-4">
                    Usamos cookies propias y de terceros para mejorar tu experiencia, analizar el tráfico y personalizar contenido. 
                    Puedes aceptar todas las cookies, rechazarlas o configurar tus preferencias.
                  </p>
                  <p className="text-[#6B7280] text-xs mb-4">
                    Consulta nuestra{' '}
                    <Link to="/politica-cookies" className="text-[#C9A24D] hover:underline">
                      Política de Cookies
                    </Link>{' '}
                    y{' '}
                    <Link to="/politica-privacidad" className="text-[#C9A24D] hover:underline">
                      Política de Privacidad
                    </Link>.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={acceptAll}
                      className="bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] rounded-full px-6 font-semibold"
                      data-testid="cookie-accept-all"
                    >
                      Aceptar todas
                    </Button>
                    <Button
                      onClick={rejectAll}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10 rounded-full px-6"
                      data-testid="cookie-reject-all"
                    >
                      Rechazar
                    </Button>
                    <Button
                      onClick={() => setShowConfig(true)}
                      variant="ghost"
                      className="text-[#9CA3AF] hover:text-white hover:bg-white/10 rounded-full px-6"
                      data-testid="cookie-configure"
                    >
                      <Gear size={18} className="mr-2" />
                      Configurar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Configurar cookies
                </h3>
                <button onClick={() => setShowConfig(false)} className="text-[#6B7280] hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-white font-medium">Cookies necesarias</p>
                    <p className="text-[#6B7280] text-sm">Esenciales para el funcionamiento de la web.</p>
                  </div>
                  <Switch checked={true} disabled className="opacity-50" />
                </div>

                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-white font-medium">Cookies de análisis</p>
                    <p className="text-[#6B7280] text-sm">Nos ayudan a entender cómo usas la web (Google Analytics).</p>
                  </div>
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, analytics: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-white font-medium">Cookies de marketing</p>
                    <p className="text-[#6B7280] text-sm">Permiten mostrarte publicidad personalizada.</p>
                  </div>
                  <Switch
                    checked={preferences.marketing}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => saveConsent(preferences)}
                  className="bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] rounded-full px-6 font-semibold"
                >
                  Guardar preferencias
                </Button>
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 rounded-full px-6"
                >
                  Aceptar todas
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Legal Pages
const LegalPageWrapper = ({ children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return children;
};

const PoliticaPrivacidad = () => (
  <LegalPageWrapper>
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-[#0B1220] py-20">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-[#C9A24D] hover:text-[#D4AF5A] mb-6 transition-colors">
            ← Volver al inicio
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Política de Privacidad
          </h1>
          <p className="text-[#9CA3AF] mt-4">Última actualización: Diciembre 2025</p>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-10">
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>1. Responsable del Tratamiento</h2>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <ul className="space-y-2 text-[#6B7280]">
                <li><strong className="text-[#0B1220]">Identidad:</strong> Ágora Hipotecas S.L.</li>
                <li><strong className="text-[#0B1220]">CIF:</strong> B-XXXXXXXX</li>
                <li><strong className="text-[#0B1220]">Dirección:</strong> Barcelona, España</li>
                <li><strong className="text-[#0B1220]">Email:</strong> agorahipotecas@gmail.com</li>
                <li><strong className="text-[#0B1220]">Teléfono:</strong> +34 676 280 792</li>
              </ul>
            </div>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>2. Finalidad del Tratamiento</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#6B7280]">
              <li>Gestionar las consultas y solicitudes de información realizadas a través del formulario de contacto.</li>
              <li>Prestar los servicios de asesoría hipotecaria contratados.</li>
              <li>Enviar comunicaciones comerciales sobre nuestros servicios, siempre que el usuario haya dado su consentimiento.</li>
              <li>Realizar análisis estadísticos de navegación para mejorar nuestros servicios.</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>3. Base Legal</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#6B7280]">
              <li><strong className="text-[#0B1220]">Consentimiento:</strong> Al rellenar y enviar el formulario de contacto.</li>
              <li><strong className="text-[#0B1220]">Ejecución de contrato:</strong> Para la prestación de servicios de asesoría hipotecaria.</li>
              <li><strong className="text-[#0B1220]">Interés legítimo:</strong> Para el envío de comunicaciones comerciales a clientes.</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>4. Conservación de Datos</h2>
            <p className="text-[#6B7280]">Los datos se conservarán mientras se mantenga la relación comercial o durante los años necesarios para cumplir con las obligaciones legales. En el caso de consultas, los datos se conservarán durante un máximo de 2 años.</p>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>5. Destinatarios de los Datos</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#6B7280]">
              <li><strong className="text-[#0B1220]">Entidades bancarias:</strong> Para gestionar las solicitudes de hipoteca, con consentimiento previo.</li>
              <li><strong className="text-[#0B1220]">Holded (CRM):</strong> Herramienta de gestión de clientes.</li>
              <li><strong className="text-[#0B1220]">Google Analytics:</strong> Para análisis estadísticos (datos anonimizados).</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>6. Derechos del Usuario</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {['Acceso', 'Rectificación', 'Supresión', 'Oposición', 'Limitación', 'Portabilidad'].map((right) => (
                <div key={right} className="bg-white p-4 rounded-xl shadow-sm">
                  <h4 className="font-semibold text-[#0B1220]">{right}</h4>
                </div>
              ))}
            </div>
            <p className="text-[#6B7280] mt-4">Para ejercer estos derechos, contacta en agorahipotecas@gmail.com adjuntando copia del DNI.</p>
          </section>
        </div>
      </main>
      <footer className="bg-[#0B1220] py-8"><div className="max-w-4xl mx-auto px-6 text-center"><p className="text-[#6B7280] text-sm">© {new Date().getFullYear()} Ágora Hipotecas</p></div></footer>
    </div>
  </LegalPageWrapper>
);

const PoliticaCookies = () => (
  <LegalPageWrapper>
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-[#0B1220] py-20">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-[#C9A24D] hover:text-[#D4AF5A] mb-6 transition-colors">
            ← Volver al inicio
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Política de Cookies
          </h1>
          <p className="text-[#9CA3AF] mt-4">Última actualización: Diciembre 2025</p>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-10">
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>1. ¿Qué son las cookies?</h2>
            <p className="text-[#6B7280]">Las cookies son pequeños archivos de texto que los sitios web almacenan en tu dispositivo cuando los visitas. Permiten que el sitio recuerde tus acciones y preferencias.</p>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>2. Tipos de cookies que utilizamos</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-3"><div className="w-3 h-3 bg-green-500 rounded-full"></div><h3 className="text-lg font-semibold text-[#0B1220]">Cookies técnicas (necesarias)</h3></div>
                <p className="text-[#6B7280]">Esenciales para el funcionamiento del sitio web.</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-3"><div className="w-3 h-3 bg-blue-500 rounded-full"></div><h3 className="text-lg font-semibold text-[#0B1220]">Cookies de análisis (Google Analytics)</h3></div>
                <p className="text-[#6B7280]">Nos permiten analizar el uso que hacen los usuarios de la web para mejorar nuestros servicios.</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-3"><div className="w-3 h-3 bg-purple-500 rounded-full"></div><h3 className="text-lg font-semibold text-[#0B1220]">Cookies de marketing</h3></div>
                <p className="text-[#6B7280]">Se utilizan para mostrar anuncios relevantes según los intereses del usuario.</p>
              </div>
            </div>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>3. Gestión del consentimiento</h2>
            <p className="text-[#6B7280]">Cuando visitas nuestra web por primera vez, te mostramos un banner donde puedes Aceptar todas, Rechazar, o Configurar tus preferencias de cookies. Tu elección se guardará durante 1 año.</p>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>4. Cómo desactivar cookies</h2>
            <p className="text-[#6B7280] mb-4">Puedes configurar tu navegador para rechazar cookies:</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[{name:'Chrome', url:'https://support.google.com/chrome/answer/95647'},{name:'Firefox', url:'https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies'},{name:'Safari', url:'https://support.apple.com/es-es/guide/safari/sfri11471/mac'},{name:'Edge', url:'https://support.microsoft.com/es-es/microsoft-edge/eliminar-cookies'}].map((b) => (
                <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow flex items-center justify-between">
                  <span className="font-medium text-[#0B1220]">{b.name}</span>
                  <span className="text-[#C9A24D] text-sm">Ver instrucciones →</span>
                </a>
              ))}
            </div>
          </section>
        </div>
      </main>
      <footer className="bg-[#0B1220] py-8"><div className="max-w-4xl mx-auto px-6 text-center"><p className="text-[#6B7280] text-sm">© {new Date().getFullYear()} Ágora Hipotecas</p></div></footer>
    </div>
  </LegalPageWrapper>
);

const AvisoLegal = () => (
  <LegalPageWrapper>
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-[#0B1220] py-20">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-[#C9A24D] hover:text-[#D4AF5A] mb-6 transition-colors">
            ← Volver al inicio
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Aviso Legal
          </h1>
          <p className="text-[#9CA3AF] mt-4">Última actualización: Diciembre 2025</p>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-10">
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>1. Datos del Titular</h2>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <ul className="space-y-2 text-[#6B7280]">
                <li><strong className="text-[#0B1220]">Denominación social:</strong> Ágora Hipotecas S.L.</li>
                <li><strong className="text-[#0B1220]">CIF:</strong> B-XXXXXXXX</li>
                <li><strong className="text-[#0B1220]">Domicilio:</strong> Barcelona, España</li>
                <li><strong className="text-[#0B1220]">Email:</strong> agorahipotecas@gmail.com</li>
                <li><strong className="text-[#0B1220]">Teléfono:</strong> +34 676 280 792</li>
              </ul>
            </div>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>2. Actividad de la Empresa</h2>
            <p className="text-[#6B7280]">Ágora Hipotecas S.L. se dedica a la prestación de servicios de asesoría y gestión hipotecaria, actuando como intermediario entre los clientes y las entidades financieras.</p>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>3. Condiciones de Uso</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#6B7280]">
              <li>Hacer un uso adecuado de los contenidos y servicios ofrecidos.</li>
              <li>No realizar actividades ilícitas o contrarias a la buena fe.</li>
              <li>No difundir contenidos de carácter ilegal.</li>
              <li>No introducir virus o programas que alteren los sistemas informáticos.</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>4. Propiedad Intelectual</h2>
            <p className="text-[#6B7280]">Todos los contenidos del sitio web son propiedad intelectual de Ágora Hipotecas S.L. o de terceros que han autorizado su uso.</p>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>5. Limitación de Responsabilidad</h2>
            <p className="text-[#6B7280]">Ágora Hipotecas S.L. no se hace responsable de los daños derivados de la falta de disponibilidad del sitio web, los contenidos de terceros, la presencia de virus, o el uso ilícito de los usuarios.</p>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-[#0B1220] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>6. Legislación Aplicable</h2>
            <p className="text-[#6B7280]">La relación entre Ágora Hipotecas S.L. y el usuario se regirá por la normativa española vigente. Las partes se someten a los Juzgados y Tribunales de Barcelona.</p>
          </section>
        </div>
      </main>
      <footer className="bg-[#0B1220] py-8"><div className="max-w-4xl mx-auto px-6 text-center"><p className="text-[#6B7280] text-sm">© {new Date().getFullYear()} Ágora Hipotecas</p></div></footer>
    </div>
  </LegalPageWrapper>
);

// Home Page Component
const HomePage = () => {
  const [showAsesoramientoWizard, setShowAsesoramientoWizard] = useState(false);

  return (
    <>
      <Navbar onOpenAsesoramiento={() => setShowAsesoramientoWizard(true)} />
      <main>
        <HeroSection onOpenAsesoramiento={() => setShowAsesoramientoWizard(true)} />
        <WhyChooseUsSection />
        <TrustSection />
        <BanksCarouselSection />
        <ServicesSection />
        <ProcessSection />
        <SimulatorSection />
        <ReviewsSection />
        <TeamSection />
        <ContactSection />
        <TikTokSection />
        <ColaboraSection />
      </main>
      <Footer />
      <WhatsAppButton />
      <AsesoramientoWizard 
        isOpen={showAsesoramientoWizard} 
        onClose={() => setShowAsesoramientoWizard(false)} 
      />
    </>
  );
};

// ============== MAIN APP ==============
function App() {
  return (
    <Router>
      <div className="App">
        <Toaster 
          position="top-center" 
          richColors 
          toastOptions={{
            style: { fontFamily: "'DM Sans', sans-serif" }
          }}
        />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/politica-cookies" element={<PoliticaCookies />} />
          <Route path="/aviso-legal" element={<AvisoLegal />} />
        </Routes>
        <CookieBanner />
      </div>
    </Router>
  );
}

export default App;
